import { Topographic } from '@journ/reckoner/topographic'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { Button } from '@thom/folio-ui/components/ui/button'
import { Input } from '@journ/bloom/input'
import { useSession, useSignIn } from '@thom/folio-ui/app/use-session'
import { useTheme } from '@thom/folio-ui/app/use-theme'
import type { AuthError } from '@thom/folio-domain/core/ports/auth'
import { useEffect, useState } from 'react'

const messageFor = (error: AuthError): string => {
	switch (error.kind) {
		case 'invalid-credentials':
			return 'Wrong email or password.'
		case 'unverified':
			return 'This account is not verified yet.'
		case 'network':
			return 'Cannot reach the server.'
		default:
			return error.message
	}
}

const Login = () => {
	const router = useRouter()
	const navigate = useNavigate()
	const signIn = useSignIn()
	const session = useSession()
	const { appearance } = useTheme()
	const [error, setError] = useState<string>()
	const [isSubmitting, setSubmitting] = useState(false)

	useEffect(() => {
		if (session.status === 'authenticated') {
			void navigate({ to: '/', replace: true })
		}
	}, [session.status, navigate])

	const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		const form = new FormData(event.currentTarget)
		setSubmitting(true)
		setError(undefined)

		const result = await signIn({
			email: String(form.get('email') ?? ''),
			password: String(form.get('password') ?? ''),
		})

		if (result.ok) {
			await router.navigate({ to: '/', replace: true })
		} else {
			setError(messageFor(result.error))
		}

		setSubmitting(false)
	}

	return (
		<main className='relative flex min-h-dvh w-full'>
			<div className='border-border bg-card relative hidden w-1/2 overflow-hidden border-r lg:block'>
				<Topographic appearance={appearance} className='absolute inset-0 size-full opacity-15' />

				<div className='pointer-events-none relative flex h-full flex-col justify-between p-12'>
					<span className='font-serif text-lg'>folio</span>
					<p className='max-w-sm font-serif text-2xl leading-snug text-balance'>“Not all those who wander are lost.”</p>
				</div>
			</div>

			<div className='flex w-full flex-col items-center justify-center p-8 lg:w-1/2 lg:p-12'>
				<div className='flex w-full max-w-md flex-1 flex-col justify-center space-y-8'>
					<div className='space-y-2 text-center'>
						<h1 className='mb-4 font-serif text-lg lg:text-xl'>Welcome</h1>
						<p className='text-dim text-sm'>Sign in to your account</p>
					</div>

					<form onSubmit={onSubmit} className='flex flex-col space-y-4'>
						<Input
							type='email'
							name='email'
							placeholder='Enter email address'
							required
							autoComplete='email'
							autoCapitalize='none'
							autoCorrect='off'
							spellCheck={false}
							aria-invalid={error ? true : undefined}
						/>

						<Input
							type='password'
							name='password'
							placeholder='Enter password'
							required
							autoComplete='current-password'
							aria-invalid={error ? true : undefined}
						/>

						{error && <p className='text-destructive text-sm'>{error}</p>}

						<Button type='submit' pending={isSubmitting} className='h-10 w-full'>
							Continue
						</Button>
					</form>
				</div>

				{/* <p className='text-dim mt-auto text-center text-xs'>
					Need an account? Ask a project owner to invite you, or{' '}
					<Link to='/login' className='underline underline-offset-4'>
						contact support
					</Link>
					.
				</p> */}
			</div>
		</main>
	)
}

export const Route = createFileRoute('/login')({
	component: Login,
})
